import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailItemExtra } from "../../../../../http/hrail/types/itemExtras";
import { useHrailItemExtrasApi } from "../../../../../http/hrail/useItemExtras";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface ItemExtraFormBody {
  item: SelectOption | null;
  extraItem: SelectOption | null;
  quantityPerItem: number | null;
}

type SelectOption = {
  id: string;
  label: string;
};

type ItemOptionSource = {
  id: string;
  code?: string | null;
  description?: string | null;
  name?: string | null;
};

interface ItemExtraFormProps {
  itemExtra?: HrailItemExtra;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
  fixedItem?: SelectOption;
  fixedExtraItem?: SelectOption;
}

export const ItemExtraForm: FC<ItemExtraFormProps> = ({
  itemExtra,
  isDisabled,
  isLoading,
  onClose,
  onCreated,
  onSaved,
  fixedItem,
  fixedExtraItem,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const itemExtraApi = useHrailItemExtrasApi();
  const itemsApi = useItemsApi();
  const [itemOptions, setItemOptions] = useState<SelectOption[]>([]);
  const itemAborter = useRef(new AbortController());
  const itemSearchTerm = useRef("");
  const loadItemOptions = createLoadOptions(itemAborter, itemSearchTerm, (query, signal) =>
    itemsApi.getAdminItems(query, signal).then((items) => items.map(toItemOption)),
  );

  const defaultValues: ItemExtraFormBody = {
    item: fixedItem ?? (itemExtra?.item ? toItemOption(itemExtra.item) : null),
    extraItem: fixedExtraItem ?? (itemExtra?.extraItem ? toItemOption(itemExtra.extraItem) : null),
    quantityPerItem: itemExtra?.quantityPerItem ?? 1,
  };

  useEffect(() => {
    loadItemOptions("").then(setItemOptions).catch(() => setItemOptions([]));
  }, []);

  const onSubmit = () => async (formData: ItemExtraFormBody) => {
    setIsSubmitting(true);

    try {
      const body = {
        itemId: fixedItem?.id ?? formData.item?.id ?? "",
        extraItemId: fixedExtraItem?.id ?? formData.extraItem?.id ?? "",
        quantityPerItem: formData.quantityPerItem ?? 1,
      };

      if (itemExtra) {
        await itemExtraApi.updateAdminHrailItemExtra(itemExtra.id, body);
        dispatch(showSuccessNotification(i18n.translation));
        onSaved?.();
        onClose?.();
      } else {
        const res = await itemExtraApi.insertAdminHrailItemExtra(body);
        dispatch(showSuccessNotification(i18n.translation));
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<ItemExtraFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, reset, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [itemExtra]);

        return (
          <FormFieldsContainer fullWidth>
            {!fixedItem && (
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <CustomAsyncSelect<SelectOption>
                        {...field}
                        inputId={labelId}
                        defaultOptions={itemOptions}
                        loadOptions={loadItemOptions}
                        isClearable={false}
                        isDisabled={isDisabled || isLoading || isSubmitting}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                        isInvalid={isInvalid}
                        onChange={(value) => setValue("item", value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
            {!fixedExtraItem && (
              <Controller
                name="extraItem"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.extraItem} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <CustomAsyncSelect<SelectOption>
                        {...field}
                        inputId={labelId}
                        defaultOptions={itemOptions}
                        loadOptions={loadItemOptions}
                        isClearable={false}
                        isDisabled={isDisabled || isLoading || isSubmitting}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                        isInvalid={isInvalid}
                        onChange={(value) => setValue("extraItem", value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
            <Controller
              name="quantityPerItem"
              control={control}
              rules={{ required: true, min: 0.000001 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.quantityPerItem} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={6}
                      allowNegative={false}
                      min={0.000001}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("quantityPerItem", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

const toItemOption = (item: ItemOptionSource): SelectOption => ({ id: item.id, label: itemLabel(item) });
